import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataTable } from "@/components/DataTable";
import { Briefcase, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const NONE = "none";
const empty = { name: "", code: "", client: "", listino_id: NONE, active: true };

export default function Commesse() {
  const [items, setItems] = useState([]);
  const [listini, setListini] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/commesse").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/listini").then((r) => setListini(r.data)); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, code: c.code || "", client: c.client || "", listino_id: c.listino_id || NONE, active: c.active }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { name: form.name, code: form.code, client: form.client, listino_id: form.listino_id === NONE ? "" : form.listino_id, active: form.active };
      if (editing) await api.put(`/commesse/${editing.id}`, payload);
      else await api.post("/commesse", payload);
      toast.success(editing ? "Commessa aggiornata" : "Commessa creata");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/commesse/${id}`); toast.success("Eliminata"); load(); } catch (e) { toast.error("Errore"); } };

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Commesse</h1>
          <p className="mt-1 text-sm text-slate-500">Cantieri/commesse con listino attivo associato.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]" data-testid="add-commessa-btn">
              <Plus className="mr-2 h-4 w-4" /> Nuova commessa
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="commessa-dialog">
            <DialogHeader><DialogTitle className="font-heading">{editing ? "Modifica commessa" : "Nuova commessa"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label htmlFor="c-name">Nome commessa</Label><Input id="c-name" data-testid="commessa-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label htmlFor="c-code">Codice</Label><Input id="c-code" data-testid="commessa-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1.5" placeholder="es. C-001" /></div>
                <div><Label htmlFor="c-client">Cliente</Label><Input id="c-client" data-testid="commessa-client-input" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} className="mt-1.5" /></div>
              </div>
              <div>
                <Label>Listino attivo</Label>
                <Select value={form.listino_id} onValueChange={(v) => setForm({ ...form, listino_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="commessa-listino-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Nessuno</SelectItem>
                    {listini.filter((l) => l.kind === "attivo").map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button onClick={save} disabled={!form.name} data-testid="save-commessa-btn" className="bg-[#002FA7] text-white hover:bg-[#00227A]">Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="rounded-lg border-slate-200 shadow-sm">
        <div className="p-4">
          <DataTable data={items} testid="commesse-table" rowTestId={(c) => `commessa-row-${c.id}`}
            searchKeys={["name", "code", "client", "listino_name"]} searchPlaceholder="Cerca commessa..." initialSort={{ key: "name", dir: "asc" }}
            emptyIcon={Briefcase} emptyText="Nessuna commessa"
            columns={[
              { key: "name", header: "Commessa", sortable: true, className: "font-medium text-slate-900", cell: (c) => c.name },
              { key: "code", header: "Codice", sortable: true, className: "text-slate-600", cell: (c) => c.code || "-" },
              { key: "client", header: "Cliente", sortable: true, className: "text-slate-600", cell: (c) => c.client || "-" },
              { key: "listino_name", header: "Listino", sortable: true, className: "text-slate-600", cell: (c) => c.listino_name || <span className="text-rose-500">— nessuno</span> },
              { key: "active", header: "Stato", sortable: true, cell: (c) => c.active ? <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">Attiva</Badge> : <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-500">Chiusa</Badge> },
              { key: "actions", header: "Azioni", align: "right", cell: (c) => (
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-commessa-${c.id}`} className="rounded-md"><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(c.id)} data-testid={`delete-commessa-${c.id}`} className="rounded-md border-rose-200 text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ) },
            ]} />
        </div>
      </Card>
    </div>
  );
}
